import { useState, useEffect, useCallback } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import api, { reportsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import CustomerDrillDownDialog from '@/components/CustomerDrillDownDialog';
import MultiSelectFilter from '@/components/MultiSelectFilter';
import { toast } from 'sonner';
import { DownloadSimple, ArrowsClockwise, X, CaretLeft, CaretRight } from '@phosphor-icons/react';

const money = (v) => (v === '' || v == null) ? '' : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtD = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '';

const COLS = [
  { h: 'Invoice Date', k: 'date', kind: 'date' },
  { h: 'Location', k: 'location', kind: 'text' },
  { h: 'Customer Name', k: 'customer_name', kind: 'customer', w: 'min-w-[170px]' },
  { h: 'Status', k: 'status', kind: 'text' },
  { h: 'Due Date', k: 'due_date', kind: 'date' },
  { h: 'Invoice#', k: 'invoice_number', kind: 'mono' },
  { h: 'Total', k: 'total', kind: 'money', align: 'right' },
  { h: 'Balance', k: 'balance', kind: 'money', align: 'right' },
  { h: 'Lead Source', k: 'lead_source', kind: 'text' },
  { h: 'Sales person', k: 'salesperson', kind: 'text' },
  { h: 'Invoice Type', k: 'invoice_type', kind: 'text' },
  { h: 'GST Treatment', k: 'gst_treatment', kind: 'text' },
  { h: 'Tax Amount', k: 'tax_amount', kind: 'money', align: 'right' },
  { h: 'Notes', k: 'notes', kind: 'text', w: 'min-w-[160px]' },
  { h: 'Receivables', k: 'receivables', kind: 'money', align: 'right' },
  { h: 'Amount Without Tax', k: 'amount_without_tax', kind: 'money', align: 'right' },
  { h: 'Doctor/SIS Name', k: 'doctor_sis_name', kind: 'text' },
  { h: 'Camp Description', k: 'camp_description', kind: 'text', w: 'min-w-[160px]' },
];

const PAGE_SIZE = 50;

export default function InvoiceReportPage() {
  const now = new Date();
  const fyStart = now.getMonth() >= 3 ? `${now.getFullYear()}-04-01` : `${now.getFullYear() - 1}-04-01`;
  const fyEnd = now.getMonth() >= 3 ? `${now.getFullYear() + 1}-03-31` : `${now.getFullYear()}-03-31`;

  const [from, setFrom] = useState(fyStart);
  const [to, setTo] = useState(fyEnd);
  const [location, setLocation] = useState('');
  const [status, setStatus] = useState('');
  const [leadSource, setLeadSource] = useState('');
  const [invoiceType, setInvoiceType] = useState('');
  const [salesperson, setSalesperson] = useState('');
  const [gstTreatment, setGstTreatment] = useState('');
  const [customer, setCustomer] = useState('');
  const [includeST, setIncludeST] = useState(false);

  const [options, setOptions] = useState({ locations: [], statuses: [], lead_sources: [], invoice_types: [], salespersons: [], gst_treatments: [] });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [drill, setDrill] = useState(null);

  useEffect(() => {
    reportsAPI.invoiceReportFilterOptions().then(({ data }) => setOptions(data)).catch(() => {});
  }, []);

  const buildParams = useCallback(() => {
    const p = { start_date: from, end_date: to, include_stock_transfer: includeST };
    if (location) p.location = location;
    if (status) p.status = status;
    if (leadSource) p.lead_source = leadSource;
    if (invoiceType) p.invoice_type = invoiceType;
    if (salesperson) p.salesperson = salesperson;
    if (gstTreatment) p.gst_treatment = gstTreatment;
    if (customer) p.customer = customer;
    return p;
  }, [from, to, includeST, location, status, leadSource, invoiceType, salesperson, gstTreatment, customer]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await reportsAPI.invoiceReport({ ...buildParams(), page, page_size: PAGE_SIZE, sort_by: sortBy, sort_dir: sortDir });
      setData(data);
    } catch { toast.error('Failed to load invoice report'); }
    finally { setLoading(false); }
  }, [buildParams, page, sortBy, sortDir]);

  useEffect(() => { load(); }, [load]);

  const applyFilters = () => { setPage(1); load(); };
  const clearFilters = () => {
    setLocation(''); setStatus(''); setLeadSource(''); setInvoiceType(''); setSalesperson('');
    setGstTreatment(''); setCustomer(''); setPage(1);
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const resp = await api.get('/reports/invoice-report/export', { params: buildParams(), responseType: 'blob' });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = `invoice_report_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel downloaded');
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortBy(key); setSortDir('desc'); }
    setPage(1);
  };

  const summary = data?.summary || {};
  const totalPages = Math.ceil((data?.total || 0) / PAGE_SIZE);
  const sortable = { date: true, total: true, balance: true, customer: true, invoice_number: true };

  const cell = (r, c) => {
    let v = r[c.k];
    if (v && typeof v === 'object') v = v.name ?? '';
    if (c.kind === 'money') return money(v);
    if (c.kind === 'date') return fmtD(v);
    return v ?? '';
  };

  return (
    <div className="p-6" data-testid="invoice-report-page">
      <PageHeader title="Invoice Report" subtitle="Invoice-level register with location, status, tax, receivables & attribution" />

      {/* Filters */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 mt-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">FROM</label>
            <Input type="date" value={from} onChange={e => setFrom(e.target.value)} className="h-8 text-xs w-36" data-testid="ir-from" />
          </div>
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">TO</label>
            <Input type="date" value={to} onChange={e => setTo(e.target.value)} className="h-8 text-xs w-36" data-testid="ir-to" />
          </div>
          <Button size="sm" variant="outline" onClick={() => { setFrom(fyStart); setTo(fyEnd); }} className="h-8 text-xs">Current FY</Button>
          <label className="flex items-center gap-2 cursor-pointer text-xs text-[#0F172A] ml-2" data-testid="ir-st-toggle">
            <input type="checkbox" checked={includeST} onChange={e => setIncludeST(e.target.checked)} className="accent-[#0F4C81]" />
            Include Stock Transfers
          </label>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <FilterSelect label="LOCATION" value={location} setValue={setLocation} options={options.locations} placeholder="All Locations" testId="ir-location" w="w-44" />
          <FilterSelect label="STATUS" value={status} setValue={setStatus} options={options.statuses} placeholder="All Status" testId="ir-status" w="w-36" />
          <FilterSelect label="LEAD SOURCE" value={leadSource} setValue={setLeadSource} options={options.lead_sources} placeholder="All Sources" testId="ir-lead-source" w="w-40" />
          <FilterSelect label="INVOICE TYPE" value={invoiceType} setValue={setInvoiceType} options={options.invoice_types} placeholder="All Types" testId="ir-invoice-type" w="w-40" />
          <FilterSelect label="SALES PERSON" value={salesperson} setValue={setSalesperson} options={options.salespersons} placeholder="All Sales Persons" testId="ir-salesperson" w="w-44" />
          <FilterSelect label="GST TREATMENT" value={gstTreatment} setValue={setGstTreatment} options={options.gst_treatments} placeholder="All" testId="ir-gst" w="w-40" />
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">CUSTOMER</label>
            <Input value={customer} onChange={e => setCustomer(e.target.value)} placeholder="Name / phone" className="h-8 text-xs w-44" data-testid="ir-customer" />
          </div>
          <div className="flex gap-2 ml-auto">
            <Button size="sm" variant="outline" onClick={clearFilters} className="h-8 text-xs"><X size={12} className="mr-1" /> Clear</Button>
            <Button size="sm" onClick={applyFilters} className="h-8 text-xs bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="ir-apply"><ArrowsClockwise size={12} className="mr-1" /> Apply</Button>
            <Button size="sm" variant="outline" onClick={exportExcel} disabled={exporting} className="h-8 text-xs" data-testid="ir-export"><DownloadSimple size={12} className="mr-1" /> {exporting ? 'Exporting…' : 'Export Excel'}</Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-4">
        {[
          { label: 'Total Invoiced', value: money(summary.total), color: 'bg-[#0F4C81] text-white', testId: 'ir-sum-total' },
          { label: 'Amount ex-Tax', value: money(summary.ex_tax), color: 'bg-[#EBF3FA] text-[#0F4C81]', testId: 'ir-sum-extax' },
          { label: 'Tax Amount', value: money(summary.tax), color: 'bg-amber-50 text-amber-700', testId: 'ir-sum-tax' },
          { label: 'Outstanding', value: money(summary.balance), color: 'bg-red-50 text-red-700', testId: 'ir-sum-balance' },
          { label: 'Invoices', value: (summary.count || 0).toLocaleString('en-IN'), color: 'bg-blue-50 text-blue-800', testId: 'ir-sum-count' },
        ].map((k, i) => (
          <div key={i} className={`rounded-xl p-4 ${k.color}`} data-testid={k.testId}>
            <p className="text-[10px] uppercase tracking-wide opacity-70">{k.label}</p>
            <p className="text-xl font-bold mt-1">{k.value}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-x-auto mt-6">
        {loading ? (
          <div className="py-16 text-center"><div className="inline-block w-6 h-6 border-2 border-[#0F4C81] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <table className="w-full text-xs whitespace-nowrap" data-testid="ir-table">
            <thead>
              <tr className="bg-[#0F4C81] text-white text-[10px] uppercase">
                {COLS.map(c => (
                  <th key={c.h} onClick={() => sortable[c.k] && toggleSort(c.k)}
                    className={`p-2.5 ${c.align === 'right' ? 'text-right' : 'text-left'} ${c.w || ''} ${sortable[c.k] ? 'cursor-pointer' : ''}`}>
                    {c.h}{sortable[c.k] && sortBy === c.k ? (sortDir === 'desc' ? ' ▼' : ' ▲') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).map((r, i) => (
                <tr key={i} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`ir-row-${i}`}>
                  {COLS.map(c => (
                    <td key={c.h} className={`p-2.5 ${c.align === 'right' ? 'text-right' : 'text-left'} ${c.kind === 'mono' ? 'font-mono text-[#0F4C81]' : ''} ${c.w ? 'max-w-[240px] truncate' : ''}`} title={c.w ? String(r[c.k] ?? '') : undefined}>
                      {c.kind === 'customer'
                        ? <button onClick={() => setDrill({ id: r.customer_id, name: r.customer_name })} className="text-[#0F4C81] underline decoration-dotted underline-offset-2 hover:text-[#0F4C81] text-left" data-testid={`ir-customer-${i}`}>{r.customer_name}</button>
                        : cell(r, c)}
                    </td>
                  ))}
                </tr>
              ))}
              {!loading && !data?.rows?.length && (
                <tr><td colSpan={COLS.length} className="text-center py-8 text-[#64748B]">No invoices match the selected filters.</td></tr>
              )}
            </tbody>
          </table>
        )}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#E2E8F0] bg-[#F8FAFC] sticky left-0">
            <span className="text-xs text-[#64748B]">Page {page} of {totalPages} · {(data?.total || 0).toLocaleString('en-IN')} invoices</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><CaretLeft size={14} /></Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><CaretRight size={14} /></Button>
            </div>
          </div>
        )}
      </div>

      <CustomerDrillDownDialog open={!!drill} onOpenChange={o => { if (!o) setDrill(null); }} customerId={drill?.id || ''} customerName={drill?.name || ''} />
    </div>
  );
}

const FilterSelect = ({ label, value, setValue, options, placeholder, testId, w }) => (
  <div>
    <label className="text-[10px] text-[#64748B] block mb-1">{label}</label>
    <MultiSelectFilter
      value={String(value || '').split(',').filter(Boolean)}
      onChange={(vals) => setValue(vals.join(','))}
      options={(options || []).map(o => ({ value: o, label: o }))}
      allLabel={placeholder} noun="selected" testid={testId}
      buttonClassName={`h-8 text-xs ${w}`}
    />
  </div>
);
